// Inlined from graphql-ruby-client/subscriptions/ActionCableLink because
// Vite 8 (rolldown) does not unwrap that package's CJS default export
// in the dev runner. Same behavior, ~40 lines, zero interop surface.
//
// Source reference:
// https://github.com/rmosolgo/graphql-ruby/blob/master/javascript_client/src/subscriptions/ActionCableLink.ts
import { ApolloLink, Observable, type Operation, type FetchResult } from "@apollo/client";
import { print } from "graphql";

interface Cable {
  subscriptions: {
    create: (
      params: Record<string, unknown>,
      handlers: {
        connected: (this: { perform: (action: string, data: unknown) => void }, args: unknown) => void;
        received: (data: { result?: FetchResult; more?: boolean }) => void;
        disconnected?: () => void;
      },
    ) => { unsubscribe: () => void };
  };
}

interface Options {
  cable: Cable;
  channelName?: string;
  actionName?: string;
  connectionParams?: Record<string, unknown> | ((operation: Operation) => Record<string, unknown>);
  callbacks?: {
    connected?: () => void;
    received?: (payload: { result?: FetchResult; more?: boolean }) => void;
    disconnected?: () => void;
  };
}

export class ActionCableLink extends ApolloLink {
  cable: Cable;
  channelName: string;
  actionName: string;
  connectionParams: Options["connectionParams"];
  callbacks: NonNullable<Options["callbacks"]>;

  constructor(options: Options) {
    super();
    this.cable = options.cable;
    this.channelName = options.channelName ?? "GraphqlChannel";
    this.actionName = options.actionName ?? "execute";
    this.connectionParams = options.connectionParams ?? {};
    this.callbacks = options.callbacks ?? {};
  }

  request(operation: Operation): Observable<FetchResult> {
    return new Observable<FetchResult>((observer) => {
      const channelId = Math.round(Date.now() + Math.random() * 100000).toString(16);
      const actionName = this.actionName;
      const connectionParams =
        typeof this.connectionParams === "function"
          ? this.connectionParams(operation)
          : this.connectionParams;
      const callbacks = this.callbacks;

      const channel = this.cable.subscriptions.create(
        {
          channel: this.channelName,
          channelId,
          ...connectionParams,
        },
        {
          connected(args: unknown) {
            this.perform(actionName, {
              query: operation.query ? print(operation.query) : null,
              variables: operation.variables,
              operationId: (operation as { operationId?: string }).operationId,
              operationName: operation.operationName,
            });
            callbacks.connected?.();
          },
          received(payload) {
            if (payload.result?.data || payload.result?.errors) {
              observer.next(payload.result);
            }
            if (!payload.more) {
              observer.complete();
            }
            callbacks.received?.(payload);
          },
          disconnected() {
            callbacks.disconnected?.();
          },
        },
      );

      return () => {
        channel.unsubscribe();
      };
    });
  }
}
